
import { annotationSourceReference, canAnnotateImage } from '@/utils/annotationSource';
import Vue, { PropType } from 'vue';
import PromptMagicButton from '@/views/Runnits/RunnitSettings/PromptMagicButton.vue';
import Prompt from '@/views/Runnits/RunnitSettings/Prompt/Prompt.vue';
import TokensSVG from '@/assets/TokensSVG.vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	getRunnitNodeRunPrice,
	RUNNIT_NODE_DEF_PRICING_TYPE,
	RUNNIT_NODE_STATIC_FIELDS_KEY,
	RunnitNodeStaticFields,
	RunnitNodeStaticFieldsKey,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	RunnitNode,
	RunnitNodeField,
	RunnitNodeRunInputValue,
	RunnitNodeRunResult,
	RunnitUpload,
	RUNNIT_NODE_FIELD_TYPE,
} from '@run-diffusion/shared';
import DynamicChipField from '@/views/Runnits/RunnitSettings/PromptDrivenForm/ChipFields/DynamicChipField.vue';
import DynamicMainActionField from '@/views/Runnits/RunnitSettings/PromptDrivenForm/MainActionFields/DynamicMainActionField.vue';
import AdvancedForm from '@/views/Runnits/RunnitSettings/PromptDrivenForm/AdvancedForm.vue';
import { RunnitBulkActionsMixin } from '@/mixins/RunnitBulkActionsMixin';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import CooldownProgressMenu from '@/views/Runnits/CooldownProgressMenu.vue';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { NODE_RUN_RESULT_SELECTION_MODE } from '@/views/Runnits/constants';
import _isNil from 'lodash/isNil';
import _cloneDeep from 'lodash/cloneDeep';
import RunRunnitButton from '@/views/Runnits/RunRunnitButton.vue';
import AutoSaveRunnitNodeRunContainer from '@/views/Runnits/RunnitSettings/AutoSaveRunnitNodeRunContainer.vue';
import Use3DTooltipCard from '@/views/Runnits/RunnitSettings/Pose3DEditor/Use3DTooltipCard.vue';
import { MyBalanceMixin, TeamUserMixin } from '@/mixins';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import VisualPromptEditor from '@/views/Runnits/RunnitSettings/VisualPromptEditor/VisualPromptEditor.vue';
import type { ImgsOriginalsMap, ImgsVisualPromptReplaceTarget } from '@/utils/imgsVisualPromptTarget';
import {
	originalEntryForTarget,
	originalImageSourceFromEntry,
	replaceImgsValueAtTarget,
	resetImgsValueAtTarget,
} from '@/utils/imgsVisualPromptTarget';

export default Vue.extend({
	name: 'PrimaryForm',
	props: {
		selectedNode: { type: Object as PropType<RunnitNode>, default: null },
		disabled: { type: Boolean, default: false },
	},
	data () {
		return {
			RUNNIT_NODE_STATIC_FIELDS_KEY,
			RUNNIT_NODE_DEF_TOOL_APP_TYPE,
			RUNNIT_NODE_FIELD_TYPE,
			promptExpanded: false,
			advancedFormOpen: false,
			formValid: false,
			showResetConfirmation: false,

			incrementAutoSaveTrigger: 0,
			runnitButtonDisabled: {
				disabled: false,
				reasons: [],
			},
			promptFieldLoading: false,
			/** IMGS field value when annotate opened; restored on annotate Cancel after reset (or any cancel). */
			annotateSessionImgsSnapshot: null as RunnitNodeRunInputValue | null,
		};
	},
	mixins: [
		RunnitBulkActionsMixin,
		RunnitsImageSelectMixin,
		MyBalanceMixin,
		TeamUserMixin,
	],
	computed: {
		...mapState([
			'team',
			'runnitState',
			'loadingTeam',
			'loadingUser',
		]),
		...mapGetters([
			'displayLabels',
		]),
		isRunnitsOwnerSelectionLoading () {
			if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
				return !!this.loadingTeam;
			}
			return !!this.loadingUser;
		},
		runnitsOwnerSelectionLabel () {
			let label: string = '';

			if (this.isRunnitsOwnerSelectionLoading) return 'Loading...';
			if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER) {
				label = 'Personal';
			} else if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
				label = this.team ? this.team.name : `No ${this.displayLabels.team} selected`;
			}

			return label;
		},
		inputValues () {
			return this.runnitState.inputValues
		},
		staticInputValues () {
			return this.runnitState.staticInputValues
		},
		determinedStaticFields () {
			if (!this.selectedNode) return {};
			return this._get(this.selectedNode, 'nodeDef.staticFields') || {};
		},
		numResultsEffectiveMax (): number {
			const numResultsField: RunnitNodeStaticFields['numResults'] = this.determinedStaticFields[RUNNIT_NODE_STATIC_FIELDS_KEY.numResults];
			const toolMax: number = numResultsField?.max ?? 1;
			if (!numResultsField) return 1;
			if (this.hasNumResultsLimitPermission && this.numResultsLimitMax) {
				return Math.min(toolMax, this.numResultsLimitMax);
			}
			return toolMax;
		},
		showNumResultsInput (): boolean {
			const numResultsField: RunnitNodeStaticFields['numResults'] = this.determinedStaticFields[RUNNIT_NODE_STATIC_FIELDS_KEY.numResults];
			if (!numResultsField) return false;
			const toolMin: number = numResultsField.min ?? 1;
			return toolMin < this.numResultsEffectiveMax;
		},
		numResultsStaticInputValue (): number {
			const numResultsField: RunnitNodeStaticFields['numResults'] = this.determinedStaticFields[RUNNIT_NODE_STATIC_FIELDS_KEY.numResults];
			const toolMin: number = numResultsField?.min ?? 1;
			const fromStaticInput: number = this.staticInputValues[RUNNIT_NODE_STATIC_FIELDS_KEY.numResults];
			const val: number = fromStaticInput > 0 ? fromStaticInput : toolMin;
			if (!this.showNumResultsInput) return toolMin;
			if (this.hasNumResultsLimitPermission && this.numResultsLimitMax) {
				return Math.min(this.numResultsEffectiveMax, Math.max(toolMin, val));
			}
			return Math.max(toolMin, val);
		},
		annotateField (): RunnitNodeField | null {
			// Find the image field (IMG or IMGS) with visual prompt enabled that has images
			if (!this.selectedNode || typeof this.selectedNode.nodeDef === 'string') {
				return null;
			}

			const findAnnotateField = (fields: any[]): RunnitNodeField | null => {
				for (const field of fields) {
					// Check if it's an image field (IMG or IMGS) with visual prompt
					const isImageField = (
						field.type === RUNNIT_NODE_FIELD_TYPE.IMGS ||
						field.type === RUNNIT_NODE_FIELD_TYPE.IMG
					) && this._get(field, 'display.hasVisualPrompt');

					if (isImageField) {
						const value = this.inputValues[field.fieldDefUuid];
						if (value) {
							if (field.type === RUNNIT_NODE_FIELD_TYPE.IMGS) {
								// IMGS field - check if it has images in arrays
								const uploads = this._get(value, 'uploads') || [];
								const nodeRunResults = this._get(value, 'nodeRunResults') || [];
								if ([...uploads, ...nodeRunResults].some(canAnnotateImage)) {
									return field;
								}
							} else if (field.type === RUNNIT_NODE_FIELD_TYPE.IMG) {
								// IMG field - check if it has a single image
								if (canAnnotateImage(this._get(value, 'nodeRunResult') || this._get(value, 'upload'))) {
									return field;
								}
							}
						}
					}
					// Check nested fields
					if (field.fields) {
						const found = findAnnotateField(field.fields);
						if (found) return found;
					}
				}
				return null;
			};

			const fields = this.selectedNode.nodeDef.fields || [];
			return findAnnotateField(fields);
		},
		showAnnotateButton (): boolean {
			return !!this.annotateField;
		},
		pose3DField (): RunnitNodeField | null {
			if (!this.selectedNode || typeof this.selectedNode.nodeDef === 'string') {
				return null;
			}

			const findPose3DField = (fields: any[]): RunnitNodeField | null => {
				for (const field of fields) {
					const isImageField = (
						field.type === RUNNIT_NODE_FIELD_TYPE.IMGS ||
						field.type === RUNNIT_NODE_FIELD_TYPE.IMG
					) && this._get(field, 'display.hasPose3D');

					if (isImageField) return field;

					if (field.fields) {
						const found = findPose3DField(field.fields);
						if (found) return found;
					}
				}
				return null;
			};

			const fields = this.selectedNode.nodeDef.fields || [];
			return findPose3DField(fields);
		},
		showUse3DButton (): boolean {
			return !!this.pose3DField;
		},
		showBatchWizardEntry (): boolean {
			const n = this.selectedNode;
			return !!(
				n &&
				n.nodeDef &&
				typeof n.nodeDef !== 'string' &&
				n.nodeDef.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.RUNNIT &&
				n.nodeDef.batchable &&
				this.hasBatchGenerationPermission
			);
		},
		getVisualPromptEditorState () {
			const states = this.runnitState.visualPromptEditorStates;
			return new Proxy(states, {
				get: (target, prop: string) => {
					return target[prop] || {
						open: false,
						saving: false,
						backgroundImageUrl: null,
						replaceImageTarget: null,
					};
				},
			});
		},
		visualPromptEditorStateForAnnotateField () {
			if (!this.annotateField) return null;
			return this.getVisualPromptEditorState[this.annotateField.uuid];
		},
		annotateVisualPromptResetRequest () {
			if (!this.annotateField) return null;
			return this.getVisualPromptEditorState[this.annotateField.uuid]?.resetToOriginalRequest || null;
		},
		/** Original for the IMGS image opened in the annotate editor (replacement mode). */
		annotateVisualPromptOriginalImage () {
			if (!this.annotateField || this.annotateField.type !== RUNNIT_NODE_FIELD_TYPE.IMGS) {
				return null;
			}
			const state = this.visualPromptEditorStateForAnnotateField;
			if (!state || !state.replaceImageTarget) {
				return null;
			}
			return originalEntryForTarget(
				this.inputValues[this.annotateField.fieldDefUuid],
				state.replaceImageTarget,
			);
		},
		/**
		 * Computed property that tracks image field values for visual prompt fields
		 * This creates a reactive dependency on the specific fields we care about
		 * Directly accesses arrays to ensure Vue tracks changes to their length
		 */
		imageFieldsWithVisualPromptValues (): string {
			if (!this.selectedNode || typeof this.selectedNode.nodeDef === 'string') {
				return '';
			}

			const getFieldValueKey = (field: any): string => {
				const value = this.inputValues[field.fieldDefUuid];
				if (!value) return '';

				if (field.type === RUNNIT_NODE_FIELD_TYPE.IMGS) {
					// Directly access arrays to ensure Vue tracks changes
					const uploads = (value.uploads || []);
					const nodeRunResults = (value.nodeRunResults || []);
					const originalImages = (value.originalImages || {});
					// Access length/keys to create reactive dependency
					const uploadsLength = uploads.length;
					const nodeRunResultsLength = nodeRunResults.length;
					const originalImagesCount = Object.keys(originalImages).length;
					return `${field.fieldDefUuid}:${uploadsLength}:${nodeRunResultsLength}:${originalImagesCount}`;
				} else if (field.type === RUNNIT_NODE_FIELD_TYPE.IMG) {
					// Directly access properties to ensure Vue tracks changes
					const hasImage = !!(value.nodeRunResult || value.upload);
					return `${field.fieldDefUuid}:${hasImage ? '1' : '0'}`;
				}
				return '';
			};

			const findFields = (fieldList: any[]): string[] => {
				const keys: string[] = [];
				for (const field of fieldList) {
					const isImageField = (
						field.type === RUNNIT_NODE_FIELD_TYPE.IMGS ||
						field.type === RUNNIT_NODE_FIELD_TYPE.IMG
					) && this._get(field, 'display.hasVisualPrompt');

					if (isImageField) {
						keys.push(getFieldValueKey(field));
					}

					if (field.fields) {
						keys.push(...findFields(field.fields));
					}
				}
				return keys;
			};

			const allFields = this.selectedNode.nodeDef.fields || [];
			return findFields(allFields).join('|');
		},
		isBatchWizardEntryDisabled (): boolean {
			return !!this.runnitButtonDisabled?.disabled;
		},
		totalRunCost () {
			if (
				this.selectedNode &&
				this.selectedNode.nodeDef &&
				typeof this.selectedNode.nodeDef !== 'string' && // Nested object is fetched
				this.selectedNode.nodeDef.pricingType === RUNNIT_NODE_DEF_PRICING_TYPE.COST &&
				this.selectedNode.nodeDef.costPerResult > 0
			) {
				const determinedFields = this.selectedNode.nodeDef.fields || [];
				return getRunnitNodeRunPrice(this.selectedNode.nodeDef.costPerResult, this.numResultsStaticInputValue, this.selectedNode.nodeDef.costPerResultLogics, this.inputValues, determinedFields);
			}
			return null; // null means can't get price yet, should disable the Runnit run button
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'updateRunnitState',
			'updateVisualPromptEditorState',
			'updatePose3DEditorState',
		]),
		async onRandomPromptClick () {
			this.$refs.primaryFormPromptField.handleGenerateRandomPrompt();
		},
		setToolsLibraryDialogOpenState (open: boolean) {
			// The change-tool affordance in single-tool mode always opens the
			// unified RunnitToolsLibraryDialog so the user has the full
			// kit-first / Kits / Tools / Apps experience. The legacy
			// kit-locked KitDialogSelect (gated on `?kit=` query param) is
			// kept around for non-change-tool entry points (intentions bar,
			// kit reroute) but is no longer wired here — it had no way to
			// switch between kits, which broke the "swap to a different kit"
			// flow once a user had arrived via the kit URL.
			this.updateRunnitState({ toolsLibraryDialogOpen: open });
		},
		openCooldownMenu () {
			this.$nextTick(() => {
				const ref = this.$refs.cooldownProgressMenu;
				if (ref && typeof ref.openMenu === 'function') ref.openMenu();
			});
		},
		async confirmReset (closeDialog: Function) {
			this.resetForm();
			closeDialog();
		},
		onInputValuesInput (newVal: Record<string, any>) {
			this.updateRunnitState({
				inputValues: {
					...this.inputValues,
					...newVal,
				},
			});
		},
		onStaticInputValuesInputChange (newVal: Record<string, any>) {
			this.updateRunnitState({
				staticInputValues: {
					...this.staticInputValues,
					...newVal,
				},
			});
		},
		onStaticInputValuesChange (key: RunnitNodeStaticFieldsKey, val: any) {
			this.onStaticInputValuesInputChange({ [key]: val });

			this.$nextTick(() => {
				// on next tick, so that the state set above has a chance to render before triggering auto save
				this.incrementAutoSaveTrigger++;
			});
		},
		onGeneratePromptFromImage (imageInput: { nodeRunResult?: any, upload?: any }) {
			// Call the generatePromptFromImage method on the prompt field
			if (this.$refs.primaryFormPromptField && this.$refs.primaryFormPromptField.generatePromptFromImage) {
				this.$refs.primaryFormPromptField.generatePromptFromImage(imageInput);
			}
		},
		syncSelectionStateForAnnotate (field: RunnitNodeField, explicit?: { uploads: RunnitUpload[]; nodeRunResults: RunnitNodeRunResult[]; originalImages?: ImgsOriginalsMap }) {
			// Restoring an unsupported original can change annotateField immediately.
			// Keep selection updates tied to the field that initiated the operation.
			const value = this.inputValues[field.fieldDefUuid];
			if (!value && !explicit) return;

			const currentUploads = explicit
				? explicit.uploads
				: (this._get(value, 'uploads') || []);
			const currentNodeRunResults = explicit
				? explicit.nodeRunResults
				: (this._get(value, 'nodeRunResults') || []);
			const originalImages = explicit && explicit.originalImages !== undefined
				? explicit.originalImages
				: (this._get(value, 'originalImages') || {});

			const selectionStateTriggerId = `multi-image-input-${field.fieldDefUuid}`;

			this.syncImgsSelectionState({
				triggeringId: selectionStateTriggerId,
				fieldAwaitingImageGallerySelection: field,
				uploads: currentUploads,
				nodeRunResults: currentNodeRunResults,
				enterMode: true,
				originalImages,
			});
		},
		async openAnnotateDialog () {
			if (!this.annotateField) return;

			// For single IMG fields, open visual prompt editor directly since we already know the image
			if (this.annotateField.type === RUNNIT_NODE_FIELD_TYPE.IMG) {
				const value = this.inputValues[this.annotateField.fieldDefUuid];
				if (!value) return;

				const nodeRunResult = this._get(value, 'nodeRunResult');
				const upload = this._get(value, 'upload');

				if (!nodeRunResult && !upload) return;
				const baseSource = annotationSourceReference(nodeRunResult || upload, nodeRunResult ? 'nodeRunResult' : 'upload');
				if (!baseSource) return;

				// Fetch the image URL
				let imageUrl: string | null = null;
				try {
					if (nodeRunResult) {
						const { imageUrl: url } = await this.fetchSrc(nodeRunResult, 'nodeRunResult');
						imageUrl = url;
					} else if (upload) {
						const { imageUrl: url } = await this.fetchSrc(upload, 'upload');
						imageUrl = url;
					}
				} catch (err) {
					console.error('Error fetching image URL for visual prompt editor:', err);
					return;
				}

				if (!imageUrl) return;

				// Close other menus globally
				this.updateRunnitState({
					promptGeneratorOpen: false,
					imageSelectOpen: false,
				});

				// Open the Visual Prompt Editor directly
				this.updateVisualPromptEditorState({
					fieldUuid: this.annotateField.uuid,
					editorState: {
						open: true,
						backgroundImageUrl: imageUrl,
						baseSource,
						replaceImageTarget: null,
					},
				});
			} else {
				// For IMGS fields, skip the selection dialog when there's only one image
				const value = this.inputValues[this.annotateField.fieldDefUuid];
				const uploads = this._get(value, 'uploads') || [];
				const nodeRunResults = this._get(value, 'nodeRunResults') || [];

				if (uploads.length + nodeRunResults.length === 1) {
					const singleUpload = uploads[0] || null;
					const singleNodeRunResult = nodeRunResults[0] || null;
					const baseSource = annotationSourceReference(singleNodeRunResult || singleUpload, singleNodeRunResult ? 'nodeRunResult' : 'upload');
					if (!baseSource) return;

					let imageUrl: string | null = null;
					try {
						if (singleNodeRunResult) {
							const { imageUrl: url } = await this.fetchSrc(singleNodeRunResult, 'nodeRunResult');
							imageUrl = url;
						} else if (singleUpload) {
							const { imageUrl: url } = await this.fetchSrc(singleUpload, 'upload');
							imageUrl = url;
						}
					} catch (err) {
						console.error('Error fetching image URL for visual prompt editor:', err);
						return;
					}

					if (!imageUrl) return;

					this.updateRunnitState({
						promptGeneratorOpen: false,
						imageSelectOpen: false,
					});

					// Set replaceImageTarget so VisualPromptEditor emits
					// 'replace-image' on save — MultiImageInputField's editor
					// only wires @replace-image.
					this.updateVisualPromptEditorState({
						fieldUuid: this.annotateField.uuid,
						editorState: {
							open: true,
							backgroundImageUrl: imageUrl,
							baseSource,
							replaceImageTarget: singleNodeRunResult
								? { kind: 'nodeRunResult', nodeRunResultUuid: singleNodeRunResult.uuid }
								: { kind: 'upload', uploadId: singleUpload.id },
						},
					});
					return;
				}

				this.syncSelectionStateForAnnotate(this.annotateField);
				this.updateRunnitState({ selectedImagesDialogOpen: true });
			}
		},
		openUse3D () {
			if (!this.pose3DField) return;

			// Mirror MultiImageInputSelectedImageContainer.onUse3D: block opening when IMGS is at cap.
			if (this.pose3DField.type === RUNNIT_NODE_FIELD_TYPE.IMGS) {
				const maxImages: number | null = this._get(this.pose3DField, 'display.maxImages') || null;
				if (maxImages) {
					const value = this.inputValues[this.pose3DField.fieldDefUuid];
					const uploads = this._get(value, 'uploads') || [];
					const nodeRunResults = this._get(value, 'nodeRunResults') || [];
					if (uploads.length + nodeRunResults.length >= maxImages) {
						this.updateSnackbar({
							status: SNACKBAR_STATUS.ERROR,
							message: `Maximum of ${maxImages} images allowed`,
							show: true,
						});
						return;
					}
				}
			}

			const input = this.$refs.pose3dFileInput as HTMLInputElement | undefined;
			if (input) input.click();
		},
		onPose3DFileSelected (ev: Event) {
			const target = ev.target as HTMLInputElement;
			const file = target.files && target.files[0];
			target.value = '';
			if (!file || !this.pose3DField) return;
			this.updatePose3DEditorState({
				fieldUuid: this.pose3DField.uuid,
				editorState: {
					open: true,
					saving: false,
					assetFile: file,
					assetName: file.name,
				},
			});
		},
		resetForm () {
			this.updateRunnitState({
				inputValues: {},
				staticInputValues: {},
			});

			// Use emitNodeRunDefaults to reset to defaults
			this.$refs.autoSaveRunnitNodeRunContainer.emitNodeRunDefaults(null, this.selectedNode);

			// Trigger auto save with empty values
			this.incrementAutoSaveTrigger++;

			// Show success message
			this.updateSnackbar({
				status: SNACKBAR_STATUS.SUCCESS,
				message: 'Form reset to defaults',
				show: true,
			});
		},
		onPromptLoadingChanged (isLoading: boolean) {
			this.promptFieldLoading = isLoading;
		},
		async onAnnotateVisualPromptResetToOriginal () {
			const target = this.visualPromptEditorStateForAnnotateField?.replaceImageTarget;
			if (!target) return;
			await this.resetAnnotateImgsTarget(target, { refreshOpenEditor: true });
		},
		/**
		 * Reset one IMGS image to its original for the annotate field. Shared by the in-editor
		 * Reset button (refreshOpenEditor) and the Selected Images dialog's per-tile Reset.
		 */
		async resetAnnotateImgsTarget (target, { refreshOpenEditor }: { refreshOpenEditor: boolean }) {
			const field = this.annotateField;
			if (!field || field.type !== RUNNIT_NODE_FIELD_TYPE.IMGS) return;
			const value = this.inputValues[field.fieldDefUuid];
			const next = resetImgsValueAtTarget(value, target);
			if (!next) return;

			this.onInputValuesInput({
				[field.fieldDefUuid]: {
					uploads: next.uploads,
					nodeRunResults: next.nodeRunResults,
					originalImages: next.originalImages,
				},
			});
			this.syncSelectionStateForAnnotate(field, {
				uploads: next.uploads,
				nodeRunResults: next.nodeRunResults,
				originalImages: next.originalImages,
			});

			if (!refreshOpenEditor) return;
			const source = originalImageSourceFromEntry(next.original);
			if (!source) return;
			const baseSource = annotationSourceReference(source.source, source.useCase);
			if (!baseSource) {
				this.updateVisualPromptEditorState({ fieldUuid: field.uuid, editorState: { open: false, backgroundImageUrl: null, baseSource: null } });
				return;
			}
			const { imageUrl } = await this.fetchSrc(source.source, source.useCase);
			this.updateVisualPromptEditorState({
				fieldUuid: field.uuid,
				editorState: {
					open: true,
					backgroundImageUrl: imageUrl,
					baseSource,
				},
			});
		},
		handleImageReplacement ({ upload, replaceImageTarget }: { upload: any, replaceImageTarget?: ImgsVisualPromptReplaceTarget }) {
			// Handle image replacement from visual prompt editor
			// For single IMG fields, we replace the entire value
			const field = this.annotateField;
			if (!field) return;

			if (field.type === RUNNIT_NODE_FIELD_TYPE.IMG) {
				const currentValue = this.inputValues[field.fieldDefUuid] || {};
				const existingOriginalImage = this._get(currentValue, 'originalImage') || null;
				const currentImageSource = this._get(currentValue, 'nodeRunResult')
					? { nodeRunResult: this._get(currentValue, 'nodeRunResult') }
					: this._get(currentValue, 'upload')
						? { upload: this._get(currentValue, 'upload') }
						: null;
				const originalImage = existingOriginalImage || currentImageSource;

				// For IMG fields, replace the upload/nodeRunResult
				this.onInputValuesInput({
					[field.fieldDefUuid]: {
						upload,
						nodeRunResult: null,
						...(originalImage && {
							originalImage,
						}),
					},
				});
			} else if (field.type === RUNNIT_NODE_FIELD_TYPE.IMGS) {
				if (!replaceImageTarget) return;
				const value = this.inputValues[field.fieldDefUuid];
				const next = replaceImgsValueAtTarget(value, replaceImageTarget, upload);
				if (!next) return;

				this.onInputValuesInput({
					[field.fieldDefUuid]: {
						uploads: next.uploads,
						nodeRunResults: next.nodeRunResults,
						originalImages: next.originalImages,
					},
				});

				// Vuex selection must match inputValues before completedReplacement patches the dialog UI
				// (same pattern as MultiImageInput — otherwise local thumbnails race ahead of selection state).
				this.syncSelectionStateForAnnotate(field, {
					uploads: next.uploads,
					nodeRunResults: next.nodeRunResults,
					originalImages: next.originalImages,
				});
				this.updateVisualPromptEditorState({
					fieldUuid: field.uuid,
					editorState: {
						completedReplacement: {
							upload,
							replaceImageTarget,
						},
					},
				});
				this.annotateSessionImgsSnapshot = null;
			}
		},
		onAnnotateVisualPromptCancel () {
			const field = this.annotateField;
			if (!field || field.type !== RUNNIT_NODE_FIELD_TYPE.IMGS) return;
			if (this.annotateSessionImgsSnapshot == null) return;
			const snap = _cloneDeep(this.annotateSessionImgsSnapshot);
			this.onInputValuesInput({
				[field.fieldDefUuid]: snap,
			});
			this.syncSelectionStateForAnnotate(field, {
				uploads: this._get(snap, 'uploads') || [],
				nodeRunResults: this._get(snap, 'nodeRunResults') || [],
				originalImages: this._get(snap, 'originalImages') || {},
			});
			this.annotateSessionImgsSnapshot = null;
		},
		onAnnotateVisualPromptSubmitDismissed () {
			if (!this.annotateField || this.annotateField.type !== RUNNIT_NODE_FIELD_TYPE.IMGS) return;
			this.annotateSessionImgsSnapshot = null;
		},
	},
	watch: {
		visualPromptEditorStateForAnnotateField: {
			deep: true,
			handler (val: { open?: boolean } | null, oldVal: { open?: boolean } | null) {
				const wasOpen = oldVal?.open;
				const isOpen = val?.open;
				if (isOpen && !wasOpen && this.annotateField?.type === RUNNIT_NODE_FIELD_TYPE.IMGS) {
					this.annotateSessionImgsSnapshot = _cloneDeep(this.inputValues[this.annotateField.fieldDefUuid] || null);
				}
			},
		},
		annotateVisualPromptResetRequest (target) {
			if (!target || !this.annotateField) return;
			// Clear first so a re-render can't replay it, then reset without opening the editor.
			this.updateVisualPromptEditorState({
				fieldUuid: this.annotateField.uuid,
				editorState: { resetToOriginalRequest: null },
			});
			this.resetAnnotateImgsTarget(target, { refreshOpenEditor: false });
		},
		/**
		 * Watch the computed property that tracks image field values
		 * This is much more efficient than watching all inputValues deeply
		 * When this changes, it means an image field with visual prompt has changed
		 */
		imageFieldsWithVisualPromptValues: {
			handler () {
				// Force update to ensure annotateField and annotateFieldKey computed properties update
				// This is needed because Vue might not detect nested array changes in inputValues
				this.$nextTick(() => {
					this.$forceUpdate();
				});
			},
		},
	},
	components: {
		PromptMagicButton,
		Prompt,
		TokensSVG,
		DynamicChipField,
		DynamicMainActionField,
		AdvancedForm,
		ConfirmDialog,
		CooldownProgressMenu,
		RunRunnitButton,
		AutoSaveRunnitNodeRunContainer,
		VisualPromptEditor,
		Use3DTooltipCard,
	},
});
